import type { Split, SplitCreatePayload, SplitUpdatePayload, SplitValueType } from '@/types/api'

export type SplitFormType = SplitValueType

export type SplitFormItem = {
  id: string
  nome: string
  doeyId: string
  tipo: SplitFormType
  porcentagem: string
  valorFixo: string
}

export const SPLIT_PERCENT_MIN = 0.1
export const SPLIT_PERCENT_MAX = 80
export const SPLIT_AMOUNT_MIN = 0.01

export function generateSplitFormId(): string {
  return `split-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function createEmptySplitFormItem(): SplitFormItem {
  return {
    id: generateSplitFormId(),
    nome: '',
    doeyId: '',
    tipo: 'percentage',
    porcentagem: '',
    valorFixo: '',
  }
}

export function isPersistedSplitId(id: string): boolean {
  return /^\d+$/.test(id)
}

export function resolveSplitType(type: string | null | undefined): SplitFormType {
  return type === 'fixed' ? 'fixed' : 'percentage'
}

export function mapApiSplitToFormItem(split: Split): SplitFormItem {
  const tipo = resolveSplitType(split.type)
  return {
    id: String(split.id),
    nome: split.name,
    doeyId: split.doey_user_id,
    tipo,
    porcentagem: tipo === 'percentage' ? String(split.percentage) : '',
    valorFixo: tipo === 'fixed' && split.amount != null ? String(split.amount) : '',
  }
}

export function mapApiSplitToNewFormItem(split: Split): SplitFormItem {
  return {
    ...mapApiSplitToFormItem(split),
    id: generateSplitFormId(),
  }
}

export function isSplitFormRowFilled(item: SplitFormItem): boolean {
  return Boolean(
    item.nome.trim() ||
      item.doeyId.trim() ||
      item.porcentagem.trim() ||
      item.valorFixo.trim()
  )
}

export function validateSplitFormItems(items: SplitFormItem[]): string | null {
  const filled = items.filter(isSplitFormRowFilled)
  const percentSum = filled.reduce((acc, item) => {
    if (item.tipo !== 'percentage') return acc
    const value = Number.parseFloat(item.porcentagem)
    return acc + (Number.isFinite(value) ? value : 0)
  }, 0)

  if (percentSum > 100) {
    return 'A soma das porcentagens não pode ultrapassar 100%.'
  }

  const errors: string[] = []
  items.forEach((item, index) => {
    if (!isSplitFormRowFilled(item)) return
    const line = index + 1
    if (!item.nome.trim() || !item.doeyId.trim()) {
      errors.push(`Linha ${line}: informe nome e ID Doey.`)
      return
    }
    if (item.tipo === 'fixed') {
      const amount = Number.parseFloat(item.valorFixo)
      if (!Number.isFinite(amount) || amount < SPLIT_AMOUNT_MIN) {
        errors.push(`Linha ${line}: informe um valor fixo maior que zero.`)
      }
      return
    }
    const percent = Number.parseFloat(item.porcentagem)
    if (!Number.isFinite(percent) || percent < SPLIT_PERCENT_MIN || percent > SPLIT_PERCENT_MAX) {
      errors.push(
        `Linha ${line}: porcentagem deve ser entre ${SPLIT_PERCENT_MIN} e ${SPLIT_PERCENT_MAX}.`
      )
    }
  })

  return errors.length > 0 ? errors.join(' ') : null
}

export function toSplitCreatePayload(item: SplitFormItem): SplitCreatePayload | null {
  if (!isSplitFormRowFilled(item)) return null

  if (item.tipo === 'fixed') {
    const amount = Number.parseFloat(item.valorFixo)
    return {
      name: item.nome.trim(),
      doey_user_id: item.doeyId.trim(),
      type: 'fixed',
      amount: Number.isFinite(amount) ? amount : 0,
    }
  }

  const percentage = Number.parseFloat(item.porcentagem)
  return {
    name: item.nome.trim(),
    doey_user_id: item.doeyId.trim(),
    type: 'percentage',
    percentage: Number.isFinite(percentage) ? percentage : 0,
  }
}

export function toSplitUpdatePayload(item: SplitFormItem): SplitUpdatePayload {
  if (item.tipo === 'fixed') {
    const amount = Number.parseFloat(item.valorFixo)
    return {
      name: item.nome.trim(),
      doey_user_id: item.doeyId.trim(),
      type: 'fixed',
      amount: Number.isFinite(amount) ? amount : 0,
    }
  }

  const percentage = Number.parseFloat(item.porcentagem)
  return {
    name: item.nome.trim(),
    doey_user_id: item.doeyId.trim(),
    type: 'percentage',
    percentage: Number.isFinite(percentage) ? percentage : 0,
  }
}

export function formatSplitValueLabel(split: Split): string {
  if (resolveSplitType(split.type) === 'fixed') {
    const amount = Number(split.amount ?? 0)
    return amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  }

  return `${split.percentage}%`
}
